import * as React from 'react';
import {useContext, useMemo} from "react";
import {CSSTransition} from 'react-transition-group';
import classNames from 'classnames';
import {observer} from "mobx-react";
import URI from 'urijs';

import {makeNounPossessive} from "../../utils/SchoolBlocksUtilities";
import {StoreContext} from "../../stores/StoreLoader";
import {AdminController, AuthenticationController, UserController} from "../../backends/YiiRoutes";

import styles from "./styles/UserMenu.module.scss";
import ClickableLink from "../utilities/ClickableLink";
import {LogoutButton} from "../admin/sidebar/items/LogoutButton";
import FontAwesome from "../utilities/FontAwesome";

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

const UserMenu: React.FC<{}> = observer(props => {
    const {userStore, interfaceStore, i18nStore, sidebarStore} = useContext(StoreContext);

    const userDetails = useMemo(() => {
        // this is intentionally removing observability from these values to prevent them from updating when logout button is clicked
        return {
            avatar: encodeURI(userStore.avatar),
            firstName: makeNounPossessive(userStore.firstName),
        }
    }, []);

    const links = [
        {
            path: UserController.myCalendar(i18nStore.currentOrgSupportedLocale),
            title: "My Calendar",
            icon: "far fa-calendar",
        },
        {
            path: UserController.following(i18nStore.currentOrgSupportedLocale),
            title: "Following",
            icon: "fas fa-star",
        },
        {
            path: UserController.feed(i18nStore.currentOrgSupportedLocale),
            title: "My Feed",
            icon: "fas fa-rss",
        },
        {
            path: UserController.settings(i18nStore.currentOrgSupportedLocale),
            title: "My Information",
            icon: "fas fa-cog",
        },
    ]

    if (userStore.isAdmin) {
        links.splice(3, 0, {
            path: AdminController.organizationSettings(i18nStore.currentOrgSupportedLocale),
            title: "Configuration",
            icon: "fas fa-cog",
        })
    }

    const topDrawerClassName = classNames({
        [styles.container]: true,
        [styles.containerOpen]: interfaceStore.userMenuOpen,
    })

    return <div className={topDrawerClassName}>
        <CSSTransition timeout={250} in={interfaceStore.userMenuOpen} classNames={transitionClassNames}>
            <div id="sb-user-drawer" className={"sb-top-drawer"}>
                <div className="sb-content-width sb-top-drawer-container">
                    <div className="sb-header-block clearfix">
                        <div className="sb-header-block-close">
                            <button onClick={interfaceStore.toggleUserMenu}><span
                                className="sr-only">Toggle the user drawer</span><span
                                className="fas fa-times fa-2x"/></button>
                        </div>
                        <div className="sb-header-block-title">
                            <h2>
                                <img className="sb-header-block-avatar" src={userDetails.avatar}
                                     alt="User avatar"/>
                                {`${userDetails.firstName} Account`}
                            </h2>
                            <p style={{margin: "0.5em auto"}}>
                                <LogoutButton className="sb-organization-color-element-font">Logout</LogoutButton>
                            </p>
                        </div>
                        <nav className="" aria-label="User Menu" role="navigation">
                            {links.map((link) => {
                                const isCurrentPath = URI(interfaceStore.currentFullUrl).path() === link.path;
                                return (
                                    <span key={link.title} className="sb-navbar-secondary-element">
                                        {link.title === "Following" ?
                                            <button className={`btn btn-default ${isCurrentPath ? "sb-organization-color-element-outline" : ""}`}
                                                    onClick={() => {
                                                        sidebarStore.setSidebar({
                                                            view: "Following"
                                                        })
                                                    }}
                                                    title={link.title}>
                                                <h4>
                                                    <span className="sb-navbar-link-icon"><span className={link.icon}/></span>
                                                    <span className="sb-navbar-link-text">{link.title}</span>
                                                </h4>
                                            </button> :
                                            <ClickableLink href={link.path}
                                                           type="button"
                                                           className={`btn btn-default ${isCurrentPath ? "sb-organization-color-element-outline" : ""}`}>
                                                <h4>
                                                    <span className="sb-navbar-link-icon"><span className={link.icon}/></span>
                                                    <span className="sb-navbar-link-text">{link.title}</span>
                                                </h4>
                                            </ClickableLink>}
                                    </span>)
                            })}
                        </nav>
                    </div>
                </div>
            </div>
        </CSSTransition>
    </div>

})

export default UserMenu;
